import type { TranslationTree } from '../../types';

export const toolView: TranslationTree = {
  toolView: {
    list: {
      pageTitle: "Sélection d'outil",
      breadcrumb: {
        tools: 'Outils',
      },
      actions: {
        refresh: 'Rafraîchir',
        scan: 'Scanner un outil',
        expandAll: 'Tout déplier',
        collapseAll: 'Replier toutes les références',
        showMore: 'Afficher plus de résultats',
      },
      tabs: {
        hierarchy: 'Vue hiérarchique',
        table: 'Vue tableau',
      },
      filters: {
        title: 'Filtres',
        reset: 'Réinitialiser',
        activeCount: '{{count}} actif',
        search: {
          label: 'Recherche',
          placeholder: "Rechercher par référence ou numéro d'outil",
        },
        reference: {
          label: 'Référence',
          all: 'Toutes les références',
        },
        health: {
          label: 'Statut de santé',
          any: 'Tous les statuts',
        },
      },
      hierarchy: {
        error: 'Impossible de charger les outils',
        retry: 'Réessayer',
        empty: {
          title: 'Aucun outil disponible',
          description: "Aucune référence ou outil n'a été trouvé dans le système.",
        },
        toolCount: '{{count}} outil(s)',
        toggleDisplay: "Basculer l'affichage de {{reference}}",
        openTool: "Ouvrir la fiche de l'outil {{toolId}}",
        noTrace: 'Jamais utilisé',
      },
      table: {
        headers: {
          tool: 'Outil',
          reference: 'Référence',
          lastTrace: 'Dernière trace',
          state: 'État',
        },
        error: 'Impossible de charger les outils',
        retry: 'Réessayer',
        empty: "Aucune référence n'est visible. Dépliez une référence pour voir les outils.",
        footer: {
          references: '{{count}} référence(s)',
          tools: '{{count}} outil(s)',
          noData: 'Aucune donnée disponible',
        },
        openTool: "Ouvrir la fiche de l'outil {{toolId}}",
        noTrace: 'Jamais utilisé',
      },
      traceCount: {
        one: 'trace',
        many: 'traces',
      },
      pagination: {
        showing: 'Affichage de {{count}} sur {{total}} éléments',
        showMore: 'Afficher plus',
      },
    },
    rul: {
      title: 'Durée de vie restante (RUL)',
      comingSoon: {
        title: 'Durée de vie restante — bientôt disponible',
        description:
          "Le modèle de maintenance prédictive n'est pas encore connecté. Cette fonctionnalité sera disponible dès que l'endpoint ML sera prêt.",
      },
    },
    addToolDialog: {
      title: "Enregistrer l'outil",
      description:
        "Cet identifiant outil n'a pas été trouvé dans la base de données. Renseignez les informations pour l'enregistrer maintenant.",
      scannedId: 'Identifiant scanné',
      fields: {
        reference: {
          label: 'Référence interne',
          placeholder: 'ex. REF-TC4-Ø12',
        },
        company: {
          label: "Nom de l'entreprise",
          placeholder: 'ex. Sandvik',
        },
      },
      actions: {
        add: "Ajouter l'outil",
        cancel: 'Annuler',
      },
      errors: {
        default: "Impossible d'enregistrer l'outil. Veuillez réessayer.",
      },
    },
    scanDialog: {
      title: 'Scanner un outil',
      description: "Choisissez votre méthode de scan préférée pour identifier l'outil.",
      helper: 'Sélectionnez un onglet ci-dessus pour commencer le scan.',
      tabs: {
        camera: 'Caméra',
        keyence: 'Keyence',
        manual: 'Manuel',
      },
      actions: {
        cancel: 'Fermer',
      },
    },
    detail: {
      description: 'Analysez la santé, les performances et les opérations associées à cet outil.',
      pageTitle: "Détails de l'outil",
      loadingSummary: "Chargement du résumé de l'outil",
      actions: {
        showList: 'Voir la liste détaillée',
        showOverview: 'Revenir à la vue synthétique',
        backToList: 'Retour à la liste',
      },
      error: {
        invalidToolId: 'Identifiant outil invalide',
        noToolIdProvided: "Aucun identifiant d'outil n'a été fourni dans l'URL.",
        authenticationRequired: 'Authentification requise',
        initializingConnection: 'Initialisation de la connexion en cours…',
      },
    },
    metadata: {
      title: 'Métadonnées outil',
      fields: {
        id: 'Identifiant',
        reference: 'Référence interne',
        state: 'État',
        machine: 'Machine la plus active',
      },
      fallback: {
        notProvided: 'Non renseigné',
      },
    },
    traces: {
      errors: {
        unknown: "Impossible de charger la traçabilité de l'outil.",
        noSummary: 'Aucun relevé exploitable trouvé pour cet outil.',
      },
      table: {
        title: 'Opérations récentes',
        description: 'Derniers relevés de traçabilité pour cet outil.',
        empty: 'Aucun relevé enregistré pour cet outil.',
        headers: {
          operator: 'Opérateur',
          duration: 'Durée',
          pieces: 'Pièces',
          date: 'Date',
          state: 'État',
          operation: 'Opération',
        },
        notProvided: 'N.D.',
      },
    },
    dataPanel: {
      title: {
        performance: 'Performance',
        conditions: "Conditions d'utilisation",
      },
      perf: {
        totalDuration: 'Durée totale',
        pieces: 'Pièces usinées',
        durationPerPiece: 'Durée / pièce',
        volume: 'Volume matière',
        distance: 'Distance',
        traces: 'Traces enregistrées',
      },
      env: {
        machineModel: 'Modèle machine',
        operation: 'Opération',
        vc: 'Vitesse de coupe (vc)',
        fz: 'Avance par dent (fz)',
        ap: 'Profondeur axiale (ap)',
        ae: 'Profondeur radiale (ae)',
        malround: 'Malrond',
        lubType: 'Type lubrifiant',
        lubBrand: 'Marque lubrifiant',
        holderType: 'Type porte-outil',
        clampingType: 'Type bridage',
      },
    },
    family: {
      mill: 'Fraise',
      drill: 'Foret',
      turning: 'Outil de tournage',
      reamer: 'Alésoir',
      tap: 'Taraud',
      tool: 'Outil',
    },
    advanced: {
      title: 'Journal détaillé des relevés',
      showEmpty: 'Afficher les champs vides',
      loading: 'Chargement des détails de traçabilité…',
      noTraces: 'Aucun relevé enregistré pour cet outil.',
      errors: {
        noDetails: 'Aucun détail supplémentaire disponible pour cet outil.',
      },
      navigator: {
        prev: 'Relevé précédent',
        next: 'Relevé suivant',
        counter: '{{current}} / {{total}}',
        viewMode: 'Voir',
        editMode: 'Modifier',
        modeLabel: "Mode d'affichage",
      },
      editMode: {
        banner: 'Modification — les changements non enregistrés seront perdus à la navigation',
        save: 'Enregistrer le relevé',
        discard: 'Annuler les modifications',
        saving: 'Enregistrement…',
        saveError: "Impossible d'enregistrer le relevé. Veuillez réessayer.",
      },
      sections: {
        general: 'Général',
        machine: 'Machine',
        material: 'Matière',
        lubrication: 'Lubrification',
        clamping: 'Bridage',
        holder: 'Porte-outil',
        breakState: 'Rupture / État',
      },
      detail: {
        machine: 'Machine',
        machineBrand: 'Marque machine',
        machineModel: 'Modèle machine',
        machineSerial: 'Numéro de série',
        machineKafkaName: 'Source des signaux',
        material: 'Matière',
        operator: 'Opérateur',
        pieces: 'Pièces usinées',
        distance: 'Distance usinée',
        volume: 'Volume retiré',
        malround: 'Malrond',
        pieceDuration: 'Durée par pièce (min)',
        totalDuration: 'Durée totale (min)',
        lubBrand: 'Marque lubrifiant',
        lubType: 'Type de lubrifiant',
        clampingBrand: 'Force de serrage',
        clampingType: 'Type de serrage',
        holderBrand: 'Marque du porte-outil',
        holderType: 'Type de porte-outil',
        operation: 'Opération',
        materialPlaceholder: 'Rechercher des matières…',
        materialSearchPlaceholder: 'Rechercher…',
        materialNoResults: 'Aucune matière trouvée',
        materialLotPlaceholder: 'Lot matière',
        materialLotSelectFirst: "Sélectionnez d'abord une matière",
        materialLotNoLots: 'Aucun lot pour cette matière',
        toolState: 'État outil',
        toolNote: 'Note',
        changeReasons: 'Motifs du changement',
      },
    },
  },
};
